import $i18n from 'panda-i18n';
import React, { forwardRef, Fragment, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, } from 'react';
import classNames from 'classnames';
import { BottomButton } from './bottom-button';
import { SelectDrawer } from './select-drawer';
import { useValue } from '@/hooks/use-value';
import { findInArray, getDataSource, isValidArray } from '@/utils/func';
import isFunction from 'lodash/isFunction';
import isString from 'lodash/isString';
import SelectBody from './section/select-body';
import SelectSearch from './section/select-search';
import SelectedListDrawer from './section/selected-list-drawer';
import SelectContext from './context';
import { getValidEvtValues, innerRenderSelection, isEqualItems } from './util';
// 自动关闭时的等待时间
const AUTO_CLOSE_WAITING_TIME = 300;
// 判断两个值是否相等
const compare = (a, b) => isEqualItems(a, b, true);
const BaseSelect = (props, ref) => {
    const { prefix = 'cn-ui-m-', placeholder, value: _value, type, defaultValue, className, mode, hasSearch, children, dataSource: _dataSource, displayType, disabled, autoConfirm, hasClear, filterLocal, transferSearchToOption, renderSelection, useDetailValue, locale: _locale, size, notFoundContent = $i18n.get({
        id: '31255240397426688.CNTM',
        dm: '无选项',
        ns: 'CnSelect',
    }), onVisibleChange, onChange = () => { }, onClear = () => { }, onCancel = () => { }, onOk = () => { }, onSearch, drawerClassName, ...others } = props;
    const [searchVal, setSearchVal] = useState('');
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [valueItemCache, setValueItemCache] = useState({});
    const clsPrefix = `${prefix}select`;
    const baseSelectRef = useRef(null);
    const realHasSearch = props.hasSearch || props.showSearch;
    const dataSource = useMemo(() => {
        return getDataSource(props.dataSource, props.children);
    }, [props.dataSource, props.children]);
    const fitValue = (v) => {
        if (v === '' || v === null) {
            return [];
        }
        const x = Array.isArray(v) ? v : [v];
        if (useDetailValue) {
            return x;
        }
        else if (isValidArray(x)) {
            return x.map((item) => {
                const realItem = 
                // eslint-disable-next-line
                typeof item === 'object' && item.hasOwnProperty('value')
                    ? item.value
                    : item;
                const tmp = findInArray(dataSource, (d) => d.value === realItem);
                // 当找到了匹配的值，且非本地搜索时，缓存一下找到的值，避免重新搜索后，在受控模式下重新调用fitValue导致数据丢失的问题
                if (tmp && realHasSearch && !filterLocal) {
                    setValueItemCache((cur) => ({
                        ...cur,
                        [realItem]: tmp,
                    }));
                }
                return (tmp ||
                    valueItemCache[realItem] || {
                    label: realItem,
                    value: realItem,
                });
            });
        }
        else {
            return [];
        }
    };
    const [value, setValue, isControlled] = useValue(props, [], {
        fitValue,
        compare,
    });
    const [innerValue, setInnerValue] = useState(value);
    const [confirmVisible, setConfirmVisible] = useState(false);
    const isSingle = mode === 'single';
    const searchRef = useRef(null);
    useMemo(() => {
        if (isControlled && !compare(value, innerValue)) {
            setInnerValue(value);
        }
    }, [value]);
    // 删除(顶部删除按钮, 自定义删除, mask)
    const handleCancel = useCallback((reason) => {
        // 情况搜索内容
        if (searchRef && searchRef.current) {
            searchRef.current.setValue('');
        }
        if (isFunction(onCancel)) {
            onCancel(reason);
        }
        setTimeout(() => {
            setInnerValue(value);
        }, 300);
    }, [value]);
    // 确认 (顶部确认,自定义确认按钮,或自动触发)
    const handleOk = () => {
        if (!isControlled) {
            setValue(innerValue);
        }
        // 判断前后两次选择是否有差异
        const changed = !isEqualItems(value, innerValue);
        if (isFunction(onOk)) {
            onOk();
        }
        if (changed && isFunction(onChange)) {
            const { value: val, items } = getValidEvtValues({
                selectedItems: innerValue,
                isSingle,
                useDetailValue,
            });
            onChange(val, 'change', items);
        }
        if (searchRef?.current) {
            searchRef.current.setValue('');
        }
    };
    const handleTotalClick = () => {
        setConfirmVisible(true);
    };
    // 清空时
    const handleClear = () => {
        if (!isControlled) {
            setValue([]);
            setInnerValue([]);
            if (searchRef?.current) {
                searchRef.current.setValue('');
            }
        }
        if (isFunction(onClear)) {
            onClear();
        }
        if (isFunction(onChange)) {
            const { value: val, items } = getValidEvtValues({
                selectedItems: [],
                useDetailValue,
                isSingle,
            });
            onChange(val, 'clear', items);
        }
    };
    // 二次确认完成
    const handleConfirmDone = (items) => {
        setInnerValue(items);
        setConfirmVisible(false);
    };
    const handleConfirmCancel = () => {
        setConfirmVisible(false);
    };
    // 底部确定按钮点击
    const handleBottomOk = () => {
        if (baseSelectRef?.current?.ok) {
            baseSelectRef.current.ok();
        }
    };
    // 底部删除按钮点击
    const handleBottomCancel = () => {
        if (baseSelectRef?.current?.cancel) {
            baseSelectRef.current.cancel('cancel-button');
        }
    };
    // 弹窗开启或关闭
    const handleVisibleChange = (visible, reason) => {
        setDrawerVisible(visible);
        if (isFunction(onVisibleChange)) {
            onVisibleChange(visible, reason);
        }
    };
    const contextValue = useMemo(() => {
        return {
            ...props,
            hasSearch: realHasSearch,
            dataSource,
            type,
            mode,
            size,
            prefix,
            value,
            innerValue,
            displayType,
            // notice: 默认直接更新 inner value, 外部更新，此组件处理
            onChange: (v) => {
                // 根据当前innerValue构建缓存，避免在实时搜索模式下丢失选项数据的问题
                if (realHasSearch && !filterLocal) {
                    setValueItemCache((cur) => {
                        const res = {};
                        if (Array.isArray(v)) {
                            v.forEach((item) => {
                                res[item.value] = item;
                            });
                            return res;
                        }
                        return cur;
                    });
                }
                setInnerValue(v);
            },
            searchValue: searchVal,
            setSearchValue: setSearchVal,
        };
    }, [
        props,
        realHasSearch,
        filterLocal,
        dataSource,
        type,
        mode,
        size,
        prefix,
        value,
        innerValue,
        displayType,
        searchVal,
    ]);
    // 单选+自动确认模式, 选项切换,自动触发确认
    useEffect(() => {
        if (drawerVisible && isSingle && autoConfirm === true) {
            setTimeout(() => {
                // 手动触发 select 的 ok 操作
                if (baseSelectRef?.current?.ok) {
                    baseSelectRef.current.ok();
                }
            }, AUTO_CLOSE_WAITING_TIME);
        }
    }, [innerValue]);
    useImperativeHandle(ref, () => Object.assign(baseSelectRef.current));
    return (React.createElement(Fragment, null,
        React.createElement(SelectDrawer, { ...others, hideButton: isSingle && autoConfirm === true, size: size, ref: baseSelectRef, showToolbar: !realHasSearch, type: type, disabled: disabled, placeholder: placeholder, hasClear: hasClear, className: classNames(clsPrefix, className), content: renderSelection
                ? renderSelection(value)
                : innerRenderSelection(value, contextValue), buttonPosition: "bottom", onClear: handleClear, onCancel: handleCancel, onOk: handleOk, onVisibleChange: handleVisibleChange, 
            // @ts-ignore
            drawerClassName: classNames(drawerClassName, {
                [`${clsPrefix}--searchable`]: realHasSearch,
            }) },
            React.createElement(SelectContext.Provider, { value: contextValue },
                React.createElement(React.Fragment, null,
                    realHasSearch && React.createElement(SelectSearch, { ref: searchRef }),
                    dataSource.length === 0 && !realHasSearch ? (
                    // 因 Provider 导致 select-drawer 空选项判断失败
                    React.createElement("div", { className: `${clsPrefix}-drawer-content ${clsPrefix}drawer-drawer--empty` }, isString(notFoundContent) ? (React.createElement("span", { className: `${clsPrefix}drawer-drawer-empty-text` }, notFoundContent)) : (React.createElement("div", { className: `${clsPrefix}drawer-drawer-empty-text` }, notFoundContent)))) : (React.createElement("div", { className: `${clsPrefix}-option-list` },
                        React.createElement(SelectBody, { emptySearchText: notFoundContent ||
                                $i18n.get({
                                    id: 'NoOptionFoundForSearchVal',
                                    dm: '找不到关于 “${searchVal}” 的选项',
                                    ns: 'CnSelect',
                                }), searchToOptionBtnText: $i18n.get({
                                id: 'AddAsOption',
                                dm: '添加为选项',
                                ns: 'CnSelect',
                            }) }))),
                    realHasSearch && !isSingle && (React.createElement("div", { className: `${clsPrefix}-drawer-footer` },
                        React.createElement(BottomButton, { okText: $i18n.get({
                                id: 'Determine',
                                dm: '确定',
                                ns: 'CnSelect',
                            }), cancelText: $i18n.get({
                                id: 'Cancel',
                                dm: '取消',
                                ns: 'CnSelect',
                            }), onOk: handleBottomOk, onCancel: handleBottomCancel }, !isSingle && (React.createElement("div", { className: `${clsPrefix}-footer-counter`, onClick: handleTotalClick },
                            React.createElement("span", null, $i18n.get({
                                id: 'CurrentlySelected',
                                dm: '当前已选中',
                                ns: 'CnSelect',
                            })),
                            React.createElement("span", { className: `${clsPrefix}-total-num` }, innerValue.length),
                            React.createElement("span", null, $i18n.get({ id: 'Item', dm: '项', ns: 'CnSelect' })))))))))),
        !isSingle && (React.createElement(SelectedListDrawer, { prefix: contextValue.prefix, innerValue: contextValue.innerValue, locale: contextValue.locale, visible: confirmVisible, onOk: handleConfirmDone, onCancel: handleConfirmCancel, renderItem: contextValue.renderItem }))));
};
const RefBaseSelect = forwardRef(BaseSelect);
RefBaseSelect.displayName = 'BaseSelect';
RefBaseSelect.defaultProps = {
    mode: 'single',
    size: 'medium',
    type: 'normal',
    displayType: 'normal',
    disabled: false,
    hideButton: false,
    transferSearchToOption: false,
    hasClear: false,
    autoConfirm: true,
    stickyOnTop: false,
};
export default RefBaseSelect;
